import { Button, Paper, Box, Grid, IconButton, Typography } from '@material-ui/core';
import { useParams } from 'react-router';
import { observer } from 'mobx-react';
import { useForeclosureSummaryStore } from '../hooks/useForeclosureSummaryStore';
import { useEffect, useState } from 'react';
import { valuationTypeOptions, dqStatusOptions } from './foreclosureOverview/foreclosureOverviewUtils';
import { StandardDialog, SelectField, CurrencyField, DataGrid, TextField, DateField, Toolbar } from '@roc/ui';
import { valuationReviewGridColumns } from './foreclosureOverview/foreclosureOverviewUtils';
import { Edit } from '@material-ui/icons';
import { formatCurrency } from '@roc/ui/utils';
import { formatDate, isNotBlank } from '@roc/client-portal-shared/utils';
import { Layout } from '@roc/feature-app-core';

export const LoanValuations = observer(() => {
  const { foreclosureSummaryStore } = useForeclosureSummaryStore();
  const { loanValuationStore } = foreclosureSummaryStore;
  const { valuationGridStore } = loanValuationStore
  const { loanId } = useParams<{ loanId: string }>();
  const { fields } = loanValuationStore.form;
  const [valuationFormModalOpen, setValuationFormModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const frameworkComponents = {
    actionsCellRenderer: params => {
      return <IconButton color='primary' onClick={() => onEditValuationHandler(params.node.data)}>
        <Edit />
      </IconButton>
    },
    currencyCellRenderer: params => (
      <Box textAlign="right">{formatCurrency(params.value)}</Box>
    ),
    typeCellRenderer: params => {
      return <>{valuationTypeOptions.find(type => type.value == params.value)?.label}</>
    },
    dateCellRenderer: params => (
      <Box textAlign="right">{formatDate(params.value, 'MM/dd/yyyy')}</Box>
    )
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    valuationGridStore.setFilterModel(filterModel);
  };

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    valuationGridStore.setSortModel(sortModel);
  };

  const afterSave = () => {
    setValuationFormModalOpen(false);
    valuationGridStore.resetAndFetchGridData();
    setEditMode(false);
  }

  useEffect(() => {
    if (!loanValuationStore.loanDetails) loanValuationStore.getLoanDetails(loanId);
    valuationGridStore.resetAndFetchGridData();
  }, [loanValuationStore.loanDetails]);

  const onAddValuationHandler = () => {
    setEditMode(false);
    loanValuationStore.reset();
    setValuationFormModalOpen(true);
  }

  const onEditValuationHandler = (selectedValuation) => {
    setEditMode(true);
    loanValuationStore.reset();
    loanValuationStore.loadForm(selectedValuation);
    setValuationFormModalOpen(true);
  }

  const onCloseHandler = () => {
    setValuationFormModalOpen(false);
  }

  const disabledFieldsForIVR = editMode && fields?.type?.value !== 'INTERNAL';
  let formModalTitle = "New Valuation";
  if (editMode) {
    formModalTitle = disabledFieldsForIVR ? "View Valuation" : "Edit Valuation";
  }

  return (
    <>
      <Paper style={{ width: '100%', padding: '30px' }}>
        <Layout title={loanValuationStore.loanDetails?.dealName}>
          <Typography variant='h4'>Property Valuations</Typography>
          <DataGrid
            frameworkComponents={frameworkComponents}
            toolbar={
              <Toolbar>
                <Button color='primary' size='small' variant='contained' onClick={onAddValuationHandler}>
                  Add Valuation
                </Button>
              </Toolbar>}
            columns={valuationReviewGridColumns}
            rows={valuationGridStore.gridData.data.rows}
            onSortChanged={onSortChanged}
            onFilterChanged={onFilterChanged}
            isLoading={valuationGridStore.isLoading}
            showFilters={valuationGridStore.showFilters}
            sortModel={valuationGridStore.sortModel}
            filterModel={valuationGridStore.filterModel}
            paginationData={valuationGridStore.gridData.meta}
            setPageNumber={valuationGridStore.setPageNumber}
            rowHeight={60}
            domLayout='autoHeight'
          />
        </Layout>
      </Paper>
      <StandardDialog
        open={valuationFormModalOpen}
        title={formModalTitle}
        maxWidth="md"
        handleClose={onCloseHandler}
        dialogContent={
          <Grid container spacing={2}>
            {loanValuationStore.loanDetails && <Grid item xs={12}>
              <SelectField standaloneLabel variant='outlined' required fullWidth label={'Property Address'} testId={'type'} options={loanValuationStore.getPropertyAddressOptions} onChange={value => loanValuationStore.onFieldChange('propertyId', value)} value={fields.propertyId.value} disabled={disabledFieldsForIVR} error={isNotBlank(fields.propertyId.error)} />
            </Grid>}
            <Grid item xs={6}>
              <SelectField standaloneLabel variant='outlined' required fullWidth label={'Valuation Type'} testId={'type'} options={valuationTypeOptions}
                onChange={value => loanValuationStore.onFieldChange('type', value)} value={fields.type.value} disabled={disabledFieldsForIVR} error={isNotBlank(fields.type.error)}
              />
            </Grid>
            <Grid item xs={6}>
              <SelectField standaloneLabel variant='outlined' required fullWidth label={'Loan DQ Status'} testId={'loanDqStatus'} options={dqStatusOptions}
                onChange={value => loanValuationStore.onFieldChange('loanDqStatus', value)} value={fields.loanDqStatus.value} disabled={disabledFieldsForIVR} error={isNotBlank(fields.loanDqStatus.error)} />
            </Grid>
            <Grid item xs={6}>
              <CurrencyField standaloneLabel variant='outlined' fullWidth testId={'arv'} label='ARV' onChange={(name, value) => loanValuationStore.onFieldChange('afterRepairValue', Number(value))}
                value={fields.afterRepairValue.value} disabled={disabledFieldsForIVR} error={isNotBlank(fields.afterRepairValue.error)} />
            </Grid>
            <Grid item xs={6}>
              <CurrencyField standaloneLabel variant='outlined' fullWidth testId={'asIs'} label='AS-IS value' onChange={(name, value) => loanValuationStore.onFieldChange('asIsValue', Number(value))}
                value={fields.asIsValue.value} disabled={disabledFieldsForIVR} error={isNotBlank(fields.asIsValue.error)} />
            </Grid>
            <Grid item xs={6}>
              <DateField format='MM/dd/yyyy' required standaloneLabel inputVariant='outlined' fullWidth label={'Date'} testId={'dqStatus'} onChange={(value) => loanValuationStore.onFieldChange('date', value)}
                value={fields.date.value} disabled={disabledFieldsForIVR} error={isNotBlank(fields.date.error)} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant='outlined'
                standaloneLabel
                fullWidth
                multiline
                minRows={4}
                maxRows={6}
                disabled={disabledFieldsForIVR}
                label='Comments'
                testId={'comments'}
                value={fields.comments.value}
                onChange={(e) => loanValuationStore.onFieldChange('comments', e.target.value)}
              />
            </Grid>
          </Grid>
        }
        dialogActions={
          <Box>
            <Button variant='outlined' color='primary' style={{ marginTop: 3, margin: 'auto 5px' }} onClick={onCloseHandler}>Cancel</Button>
            {!disabledFieldsForIVR && <Button variant='contained' color='primary' style={{ marginTop: 3, margin: 'auto 5px' }} onClick={() => loanValuationStore.saveValuationForm(afterSave)}>Save</Button>}
          </Box>
        }
      />
    </>
  );
});